import React from 'react';
import { ListColumn } from '../Datalist/Datalist'; // Import your Datalist component
import Datalist from '../Datalist/Datalist';
import './MemberTable.scss';
import { format } from 'date-fns';
import { FaCheckSquare, FaSquare } from 'react-icons/fa';

interface Member {
    selected: boolean;
    customerOrgId: string;
    customerName: string;
    contactPerson: string;
    lastReviewed: string;
}

interface MemberTableProps {
    memberData: Member[];
    isLoading: boolean;
    onCheckboxChange: (orgId: string) => void;
    hideCheckboxes?: boolean; // Add a prop to control whether checkboxes should be shown
    isEditable: boolean; // Add a prop to toggle whether contact persons can be edited
    onContactPersonChange: (orgId: string, newEmail: string) => void; // Add a prop to handle email changes
}

const MemberTable: React.FC<MemberTableProps> = ({
    memberData,
    isLoading,
    onCheckboxChange,
    hideCheckboxes = false,
    isEditable,
    onContactPersonChange,
}) => {
    // Define the columns for the Member table
    const columns: ListColumn<Member>[] = React.useMemo(() => {
        const baseColumns: ListColumn<Member>[] = [
            {
                key: 'customerOrgId',
                fieldName: 'customerOrgId',
                name: 'Kund',
                minWidth: '32%',
                maxWidth: '32%',
                isActive: true,
                sorting: true,
                onRender: (item: Member) => `${item.customerName} - ${item.customerOrgId}`,
            },
            {
                key: 'contactPerson',
                fieldName: 'contactPerson',
                name: 'Kontakt Person',
                minWidth: '32%',
                maxWidth: '32%',
                isActive: true,
                sorting: true,
                onRender: (item: Member) => {
                    return isEditable ? (
                        <input
                            type="email"
                            value={item.contactPerson}
                            onChange={(e) => onContactPersonChange(item.customerOrgId, e.target.value)}
                            className="editable-contact-email"
                            style={{ border: '1px solid #ced4da', padding: '5px', width: '100%' }}
                        />
                    ) : (
                        <span className={item.contactPerson === 'Epost saknas' ? 'missing-email' : ''}>
                            {item.contactPerson}
                        </span>
                    );
                },
            },
            {
                key: 'lastReviewed',
                fieldName: 'lastReviewed',
                name: 'Senast Kontaktad',
                minWidth: '32%',
                maxWidth: '32%',
                isActive: true,
                sorting: true,
                onRender: (item: Member) => {
                    const lastReviewedDate = new Date(item.lastReviewed);
                    return isNaN(lastReviewedDate.getTime()) ? 'N/A' : format(lastReviewedDate, 'yyyy-MM-dd');
                },
            },
        ];

        // Add the checkbox column only if hideCheckboxes is false
        if (!hideCheckboxes) {
            baseColumns.unshift({
                key: 'selected',
                fieldName: 'selected',
                name: '',
                minWidth: '4%',
                maxWidth: '4%',
                isActive: true,
                sorting: false,
                onRender: (item: Member) => (
                    <div
                        className="checkbox-container"
                        onClick={() => {
                            if (item.contactPerson !== 'Epost saknas') {
                                onCheckboxChange(item.customerOrgId);
                            }
                        }}
                    >
                        {item.contactPerson === 'Epost saknas' ? (
                            <FaSquare className="custom-checkbox disabled" />
                        ) : item.selected ? (
                            <FaCheckSquare className="custom-checkbox checked" />
                        ) : (
                            <FaSquare className="custom-checkbox" />
                        )}
                    </div>
                ),
                footer: (items: Member[]) => `Totalt antal rader: ${items.length}`,
            });
        }

        return baseColumns;
    }, [memberData, onCheckboxChange, hideCheckboxes, isEditable]); // Include isEditable in the dependency array

    return (
        <div className="member-table-wrapper">
            <Datalist
                items={memberData}
                isLoading={isLoading}
                columns={columns}
                setColumns={(updatedColumns: ListColumn<Member>[]) => {
                    console.log('Columns updated:', updatedColumns);
                }}
                initialSortKey="lastReviewed"
                descendingSort={true}
                noItemsText="Inga medlemmar tillgängliga för vald period"
                uniqueKey="customerOrgId"
                isEditable={isEditable} // Pass the editable state to Datalist
                onEditCell={(key, value, item) => {
                    if (key === 'contactPerson') {
                        onContactPersonChange(item.customerOrgId, value);
                    }
                }}
            />
        </div>
    );
};

export default MemberTable;
